/**
 * A theme = one settings block per mode:
 *   primary   accent colour (buttons, links, active items)
 *   secondary page background
 *   third     text colour
 *   sidebar   side panel (menu) background
 *   sidebar_text side panel text colour
 *   size_primary   body text (px)
 *   size_secondary small text: labels, meta, badges (px)
 *   size_third     heading text (px)
 * Everything else (cards, borders, muted text …) is derived from these.
 */
export const DEFAULTS = {
  light: { primary: '#e11d48', secondary: '#f6f7f9', third: '#0f172a', sidebar: '#ffffff', sidebar_text: '#0f172a', size_primary: 14, size_secondary: 12.5, size_third: 24 },
  dark: { primary: '#fb7185', secondary: '#0b0e14', third: '#f1f5f9', sidebar: '#131823', sidebar_text: '#f1f5f9', size_primary: 14, size_secondary: 12.5, size_third: 24 },
};

export const SIZE_LIMITS = {
  size_primary: [12, 18],
  size_secondary: [10, 16],
  size_third: [18, 36],
};

const HEX = /^#[0-9a-fA-F]{6}$/;

function rgb(hex) {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

function toHex(c) {
  return `#${c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`;
}

/** Mix `a` toward `b` by `t` (0 = a, 1 = b). */
export function mix(a, b, t) {
  const x = rgb(a);
  const y = rgb(b);
  return toHex(x.map((v, i) => v + (y[i] - v) * t));
}

function alpha(hex, a) {
  const [r, g, b] = rgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function luminance(hex) {
  const f = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgb(hex).map(f);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

const surfaceOf = (bg) => (luminance(bg) > 0.5 ? mix(bg, '#ffffff', 0.85) : mix(bg, '#ffffff', 0.06));

/** Settings with anything missing / invalid replaced by the mode's defaults. */
export function normalise(settings, mode) {
  const d = DEFAULTS[mode];
  const out = { ...d };
  ['primary', 'secondary', 'third', 'sidebar', 'sidebar_text'].forEach((k) => {
    if (HEX.test(settings?.[k] || '')) out[k] = settings[k];
  });
  // Themes saved before the side panel colour existed: follow the card colour of their background.
  if (!HEX.test(settings?.sidebar || '') && HEX.test(settings?.secondary || '')) {
    out.sidebar = surfaceOf(out.secondary);
  }
  // No side panel text colour saved: use the theme text colour if it reads, else black/white.
  if (!HEX.test(settings?.sidebar_text || '') && (HEX.test(settings?.third || '') || HEX.test(settings?.sidebar || ''))) {
    out.sidebar_text = contrast(out.sidebar, out.third) >= 4.5
      ? out.third
      : luminance(out.sidebar) > 0.5 ? '#0f172a' : '#f8fafc';
  }
  Object.entries(SIZE_LIMITS).forEach(([k, [min, max]]) => {
    const n = Number(settings?.[k]);
    if (Number.isFinite(n)) out[k] = Math.min(max, Math.max(min, n));
  });
  return out;
}

/** The CSS custom properties for one mode's settings. */
export function cssVars(settings, mode) {
  const s = normalise(settings, mode);
  const bg = s.secondary;
  const ink = s.third;
  const lightBg = luminance(bg) > 0.5;
  const sb = s.sidebar;
  const sbLight = luminance(sb) > 0.5;
  const sbInk = s.sidebar_text;

  return {
    '--brand': s.primary,
    '--brand-600': mix(s.primary, lightBg ? '#000000' : '#ffffff', 0.18),
    '--brand-soft': alpha(s.primary, lightBg ? 0.09 : 0.12),
    '--brand-ring': alpha(s.primary, lightBg ? 0.28 : 0.35),
    '--on-brand': luminance(s.primary) > 0.45 ? '#0f172a' : '#ffffff',

    '--bg': bg,
    '--surface': lightBg ? mix(bg, '#ffffff', 0.85) : mix(bg, '#ffffff', 0.06),
    '--surface-2': lightBg ? mix(bg, '#ffffff', 0.5) : mix(bg, '#ffffff', 0.09),
    '--ink': ink,
    '--ink-2': mix(ink, bg, 0.22),
    '--muted': mix(ink, bg, 0.45),
    '--border': mix(bg, ink, lightBg ? 0.1 : 0.13),
    '--border-strong': mix(bg, ink, lightBg ? 0.2 : 0.24),

    '--sidebar-bg': sb,
    '--sidebar-ink': sbInk,
    '--sidebar-ink-2': mix(sbInk, sb, 0.22),
    '--sidebar-muted': mix(sbInk, sb, 0.45),
    '--sidebar-border': mix(sb, sbInk, sbLight ? 0.1 : 0.13),
    '--sidebar-hover': mix(sb, sbInk, sbLight ? 0.06 : 0.09),

    '--fs-body': String(s.size_primary / 14),
    '--fs-small': String(s.size_secondary / 12.5),
    '--fs-title': String(s.size_third / 24),
  };
}

export const isDefault = (settings, mode) =>
  JSON.stringify(normalise(settings, mode)) === JSON.stringify(DEFAULTS[mode]);
